export interface PackingSlipRow {
  quantity: number;
  rollsAmount: number;
  stickersPerRoll: number;
}

export interface PackingSlipPackage {
  packageQuantity: number;
  rows: PackingSlipRow[];
}

export interface PackingSlipApiPackage {
  packageQuantity: number;
  rollsAmount: number;
  stickersPerRoll: number;
}

export interface PackingSlipNestedApiPackage {
  packageNumber: number;
  packageQuantity: number;
  rollsAmount: number;
  stickersPerRoll: number;
  rows: PackingSlipRow[];
}

export const DELIVERY_TICKET_SIZE_150X100_V2 = "150x100_V2";

export const isDeliveryTicketSize150x100V2 = (
  size: string | undefined | null,
): boolean => size === DELIVERY_TICKET_SIZE_150X100_V2;

const toPositiveInt = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.floor(value));
};

export const distributeQuantity = (total: number, count: number): number[] => {
  const safeCount = Math.floor(count);
  if (!Number.isFinite(count) || safeCount <= 0) {
    return [];
  }

  const safeTotal = toPositiveInt(total);
  if (safeTotal === 0) {
    return Array(safeCount).fill(0);
  }

  const base = Math.ceil(safeTotal / safeCount);
  const lastQuantity = safeTotal - base * (safeCount - 1);

  if (lastQuantity < 0) {
    const floorBase = Math.floor(safeTotal / safeCount);
    const remainder = safeTotal % safeCount;
    return Array.from({ length: safeCount }, (_, index) =>
      index < remainder ? floorBase + 1 : floorBase,
    );
  }

  return Array.from({ length: safeCount }, (_, index) =>
    index === safeCount - 1 ? lastQuantity : base,
  );
};

export const distributeQuantityWithBase = (
  total: number,
  count: number,
  base: number,
): number[] => {
  const safeCount = Math.floor(count);
  const safeTotal = toPositiveInt(total);
  const safeBase = toPositiveInt(base);

  if (safeCount <= 0) {
    return [];
  }

  if (safeBase <= 0) {
    return distributeQuantity(safeTotal, safeCount);
  }

  const lastQuantity = safeTotal - safeBase * (safeCount - 1);
  if (lastQuantity < 0) {
    return distributeQuantity(safeTotal, safeCount);
  }

  return Array.from({ length: safeCount }, (_, index) =>
    index === safeCount - 1 ? lastQuantity : safeBase,
  );
};

export const createDefaultRow = (quantity: number): PackingSlipRow => {
  const safeQuantity = toPositiveInt(quantity);
  return {
    quantity: safeQuantity,
    rollsAmount: 1,
    stickersPerRoll: safeQuantity,
  };
};

const countOrFallback = (value: number): number =>
  Number.isFinite(value) && value > 0 ? value : 1;

export const createDistributedRows = (
  packageQuantity: number,
  rowsCount: number,
): PackingSlipRow[] => {
  const safeRowsCount = Math.max(1, Math.floor(countOrFallback(rowsCount)));
  const quantities = distributeQuantity(packageQuantity, safeRowsCount);
  return quantities.map((quantity) => createDefaultRow(quantity));
};

export const createPackages = (
  totalQuantity: number,
  packagesCount: number,
  options?: {
    previousPackages?: PackingSlipPackage[];
    preserveRowCounts?: boolean;
    baseQuantity?: number;
  },
): PackingSlipPackage[] => {
  const quantities =
    options?.baseQuantity && options.baseQuantity > 0
      ? distributeQuantityWithBase(
          totalQuantity,
          packagesCount,
          options.baseQuantity,
        )
      : distributeQuantity(totalQuantity, packagesCount);

  return quantities.map((packageQuantity, index) => {
    const previousRowCount = options?.previousPackages?.[index]?.rows?.length;
    const rowCount =
      options?.preserveRowCounts && previousRowCount
        ? previousRowCount
        : 1;

    return {
      packageQuantity,
      rows: createDistributedRows(packageQuantity, rowCount),
    };
  });
};

export const redistributePackageRows = (
  packingPackage: PackingSlipPackage,
  rowsCount: number,
): PackingSlipPackage => {
  const safeRowsCount = Math.max(1, Math.floor(countOrFallback(rowsCount)));
  return {
    packageQuantity: packingPackage.packageQuantity,
    rows: createDistributedRows(packingPackage.packageQuantity, safeRowsCount),
  };
};

export const calculateRowQuantity = (
  rollsAmount: number,
  stickersPerRoll: number,
): number => {
  const safeRolls = toPositiveInt(rollsAmount);
  const safeStickers = toPositiveInt(stickersPerRoll);
  return safeRolls * safeStickers;
};

const getOtherRowsQuantitySum = (
  rows: PackingSlipRow[],
  rowIndex: number,
): number =>
  rows.reduce(
    (sum, row, index) => (index === rowIndex ? sum : sum + row.quantity),
    0,
  );

const applyLastRowRemainder = (
  packingPackage: PackingSlipPackage,
  rows: PackingSlipRow[],
): PackingSlipRow[] => {
  if (rows.length <= 1) {
    return rows;
  }

  const lastIndex = rows.length - 1;
  const sumExceptLast = rows
    .slice(0, lastIndex)
    .reduce((sum, row) => sum + row.quantity, 0);
  const lastQuantity = packingPackage.packageQuantity - sumExceptLast;
  const lastRow = rows[lastIndex];

  lastRow.quantity = Math.max(0, lastQuantity);
  lastRow.stickersPerRoll =
    lastRow.rollsAmount > 0
      ? Math.floor(lastRow.quantity / lastRow.rollsAmount)
      : lastRow.quantity;

  return rows;
};

const capRowToRemainingQuantity = (
  row: PackingSlipRow,
  maxAllowed: number,
): PackingSlipRow => {
  const product = calculateRowQuantity(row.rollsAmount, row.stickersPerRoll);
  if (product <= maxAllowed) {
    return {
      ...row,
      quantity: product,
    };
  }

  const cappedStickers =
    row.rollsAmount > 0 ? Math.floor(maxAllowed / row.rollsAmount) : maxAllowed;

  return {
    ...row,
    stickersPerRoll: Math.max(0, cappedStickers),
    quantity: calculateRowQuantity(row.rollsAmount, Math.max(0, cappedStickers)),
  };
};

export const updateRowFromRollsAndStickers = (
  packingPackage: PackingSlipPackage,
  rowIndex: number,
  field: "rollsAmount" | "stickersPerRoll",
  value: number,
): PackingSlipPackage | null => {
  if (
    rowIndex < 0 ||
    rowIndex >= packingPackage.rows.length ||
    !Number.isFinite(value) ||
    value < 0
  ) {
    return null;
  }

  const nextRows = packingPackage.rows.map((row) => ({ ...row }));
  const currentRow = nextRows[rowIndex];
  const safeValue = toPositiveInt(value);
  currentRow[field] = safeValue;

  if (nextRows.length === 1) {
    currentRow.quantity = packingPackage.packageQuantity;
    if (field === "rollsAmount") {
      currentRow.stickersPerRoll =
        currentRow.rollsAmount > 0
          ? Math.floor(packingPackage.packageQuantity / currentRow.rollsAmount)
          : packingPackage.packageQuantity;
    } else if (currentRow.stickersPerRoll > 0) {
      currentRow.rollsAmount = Math.max(
        1,
        Math.floor(packingPackage.packageQuantity / currentRow.stickersPerRoll),
      );
    }
    return {
      ...packingPackage,
      rows: nextRows,
    };
  }

  const maxAllowed = Math.max(
    0,
    packingPackage.packageQuantity - getOtherRowsQuantitySum(nextRows, rowIndex),
  );

  if (field === "rollsAmount") {
    if (currentRow.rollsAmount > 0) {
      currentRow.stickersPerRoll = Math.floor(
        currentRow.quantity / currentRow.rollsAmount,
      );
    }
    nextRows[rowIndex] = capRowToRemainingQuantity(currentRow, maxAllowed);
  } else {
    nextRows[rowIndex] = capRowToRemainingQuantity(currentRow, maxAllowed);
  }

  applyLastRowRemainder(packingPackage, nextRows);

  return {
    ...packingPackage,
    rows: nextRows,
  };
};

export const updatePackageQuantityKeepingTotal = (
  packages: PackingSlipPackage[],
  packageIndex: number,
  newQuantity: number,
  totalQuantity: number,
): PackingSlipPackage[] | null => {
  if (
    packageIndex < 0 ||
    packageIndex >= packages.length ||
    !Number.isFinite(newQuantity) ||
    newQuantity <= 0
  ) {
    return null;
  }

  const safeQuantity = toPositiveInt(newQuantity);
  const nextPackages = packages.map((packingPackage) => ({
    ...packingPackage,
    rows: packingPackage.rows.map((row) => ({ ...row })),
  }));

  const applyQuantity = (
    packingPackage: PackingSlipPackage,
    packageQuantity: number,
  ): PackingSlipPackage =>
    redistributePackageRows(
      { ...packingPackage, packageQuantity },
      packingPackage.rows.length,
    );

  if (nextPackages.length === 1) {
    if (safeQuantity !== totalQuantity) {
      return null;
    }
    nextPackages[0] = applyQuantity(nextPackages[0], totalQuantity);
    return nextPackages;
  }

  const lastIndex = nextPackages.length - 1;

  if (packageIndex === lastIndex) {
    const sumOfMiddlePackages = nextPackages
      .slice(1, lastIndex)
      .reduce((sum, packingPackage) => sum + packingPackage.packageQuantity, 0);
    const firstQuantity = totalQuantity - safeQuantity - sumOfMiddlePackages;
    if (firstQuantity <= 0) {
      return null;
    }
    nextPackages[0] = applyQuantity(nextPackages[0], firstQuantity);
    nextPackages[lastIndex] = applyQuantity(nextPackages[lastIndex], safeQuantity);
    return nextPackages;
  }

  nextPackages[packageIndex] = applyQuantity(
    nextPackages[packageIndex],
    safeQuantity,
  );

  const sumWithoutLast = nextPackages
    .slice(0, lastIndex)
    .reduce((sum, packingPackage) => sum + packingPackage.packageQuantity, 0);
  const lastQuantity = totalQuantity - sumWithoutLast;
  if (lastQuantity <= 0) {
    return null;
  }

  nextPackages[lastIndex] = applyQuantity(nextPackages[lastIndex], lastQuantity);
  return nextPackages;
};

export const updateRowQuantityKeepingPackageTotal = (
  packingPackage: PackingSlipPackage,
  rowIndex: number,
  newQuantity: number,
): PackingSlipPackage | null => {
  if (
    rowIndex < 0 ||
    rowIndex >= packingPackage.rows.length ||
    !Number.isFinite(newQuantity) ||
    newQuantity < 0
  ) {
    return null;
  }

  const nextRows = packingPackage.rows.map((row) => ({ ...row }));
  const lastIndex = nextRows.length - 1;
  const safeQuantity = toPositiveInt(newQuantity);

  if (nextRows.length === 1) {
    nextRows[0] = {
      ...nextRows[0],
      quantity: packingPackage.packageQuantity,
      stickersPerRoll: packingPackage.packageQuantity,
    };
    return {
      packageQuantity: packingPackage.packageQuantity,
      rows: nextRows,
    };
  }

  if (rowIndex === lastIndex) {
    return packingPackage;
  }

  nextRows[rowIndex] = {
    ...nextRows[rowIndex],
    quantity: safeQuantity,
    stickersPerRoll: safeQuantity,
  };

  const sumWithoutLast = nextRows
    .slice(0, lastIndex)
    .reduce((sum, row) => sum + row.quantity, 0);
  const lastQuantity = packingPackage.packageQuantity - sumWithoutLast;

  if (lastQuantity < 0) {
    return null;
  }

  nextRows[lastIndex] = {
    ...nextRows[lastIndex],
    quantity: lastQuantity,
    stickersPerRoll: lastQuantity,
  };

  return {
    packageQuantity: packingPackage.packageQuantity,
    rows: nextRows,
  };
};

export const sumPackageQuantities = (packages: PackingSlipPackage[]): number =>
  packages.reduce((sum, packingPackage) => sum + packingPackage.packageQuantity, 0);

export const isPackagesDistributionValid = (
  totalQuantity: number,
  packages: PackingSlipPackage[],
  isLabelInRolls: boolean,
): boolean => {
  if (!Number.isFinite(totalQuantity) || totalQuantity <= 0 || packages.length === 0) {
    return false;
  }

  if (packages.some((packingPackage) => !Number.isFinite(packingPackage.packageQuantity) || packingPackage.packageQuantity <= 0)) {
    return false;
  }

  if (sumPackageQuantities(packages) !== totalQuantity) {
    return false;
  }

  if (!isLabelInRolls) {
    return true;
  }

  return packages.every((packingPackage) => {
    if (!packingPackage.rows.length) {
      return false;
    }

    const rowsSum = packingPackage.rows.reduce((sum, row) => sum + row.quantity, 0);
    if (rowsSum !== packingPackage.packageQuantity) {
      return false;
    }

    return packingPackage.rows.every(
      (row) =>
        Number.isFinite(row.quantity) &&
        row.quantity > 0 &&
        Number.isFinite(row.rollsAmount) &&
        row.rollsAmount >= 0 &&
        Number.isFinite(row.stickersPerRoll) &&
        row.stickersPerRoll >= 0,
    );
  });
};

export const buildPackagesPayload = (
  packages: PackingSlipPackage[],
  isLabelInRolls: boolean,
): PackingSlipApiPackage[] => {
  if (!isLabelInRolls) {
    return packages.map((packingPackage) => ({
      packageQuantity: packingPackage.packageQuantity,
      rollsAmount: 0,
      stickersPerRoll: 0,
    }));
  }

  return packages.flatMap((packingPackage) =>
    packingPackage.rows.map((row) => ({
      packageQuantity: row.quantity,
      rollsAmount: row.rollsAmount,
      stickersPerRoll: row.stickersPerRoll,
    })),
  );
};

export const buildNestedPackagesPayload = (
  packages: PackingSlipPackage[],
  isLabelInRolls: boolean,
): PackingSlipNestedApiPackage[] =>
  packages.map((packingPackage, index) => {
    const rows = isLabelInRolls
      ? packingPackage.rows.map((row) => ({
          quantity: row.quantity,
          rollsAmount: row.rollsAmount,
          stickersPerRoll: row.stickersPerRoll,
        }))
      : [
          {
            quantity: packingPackage.packageQuantity,
            rollsAmount: 0,
            stickersPerRoll: 0,
          },
        ];

    return {
      packageNumber: index + 1,
      packageQuantity: packingPackage.packageQuantity,
      rollsAmount: rows[0]?.rollsAmount ?? 0,
      stickersPerRoll: rows[0]?.stickersPerRoll ?? 0,
      rows,
    };
  });
